import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { BRANDS, BRANDS_PAGE_LOGOS } from "@/data/site";
import BrandCard from "@/components/BrandCard";
import Reveal from "@/components/Reveal";

export default function Brands() {
  return (
    <div data-testid="page-brands" className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-12 bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline overline-green">The Native Touch Family</div>
              <h1 className="font-serif-display text-4xl md:text-6xl mt-5 leading-[1.05] text-[var(--ink)] font-semibold">
                Four Brands. <span className="text-[var(--gold)]">One Food Ecosystem.</span>
              </h1>
              <div className="flex items-center gap-3 justify-center mt-6">
                <span className="h-[1px] w-12 bg-[var(--gold)]/60" />
                <p className="text-sm text-[var(--ink-2)]">One platform. Multiple formats. One premium millet movement.</p>
                <span className="h-[1px] w-12 bg-[var(--gold)]/60" />
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-12">
            {BRANDS.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <BrandCard brand={b} logoOverride={BRANDS_PAGE_LOGOS[b.slug]} premium />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILED BRAND ROWS */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 space-y-20">
          {BRANDS.map((b, i) => {
            const chipBg = b.slug === "stories" ? "#f4e9d4" : "#0d3a20";
            return (
            <Reveal key={b.slug}>
              <article className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-7">
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-[var(--bg-cream)] relative">
                    <img src={b.hero} alt={b.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <div
                    className="inline-flex items-center justify-center rounded-xl px-6 py-4 mb-6 border border-[var(--line-soft)] shadow-sm"
                    style={{ background: chipBg }}
                  >
                    <img
                      src={BRANDS_PAGE_LOGOS[b.slug]}
                      alt={`${b.name} logo`}
                      className="h-16 md:h-20 w-auto object-contain"
                    />
                  </div>
                  <div className="overline overline-green">{b.category}</div>
                  <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
                    {b.tagline}
                  </h2>
                  <p className="mt-5 text-[var(--ink-2)] leading-relaxed">{b.story}</p>
                  <ul className="mt-6 grid grid-cols-2 gap-3">
                    {b.pillars.slice(0, 4).map((p) => (
                      <li key={p.label} className="flex items-start gap-2 text-sm text-[var(--ink-2)]">
                        <CheckCircle2 size={16} className="text-[var(--gold)] mt-0.5 shrink-0" />
                        <span><strong className="text-[var(--green)] font-semibold">{p.label}</strong> — {p.text}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link to={`/brands/${b.slug}`} data-testid={`brands-cta-${b.slug}`} className="btn-primary">
                      Explore {b.name.split("—")[0].trim()} <ArrowRight size={14} />
                    </Link>
                    <a href={b.site} data-testid={`brands-external-${b.slug}`} className="btn-outline">
                      Visit Official Site <ArrowUpRight size={14} />
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          );})}
        </div>
      </section>
    </div>
  );
}
